import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addFreeDaysToPortalSubscription,
  cancelPortalSubscription,
  getPortalPauseOptions,
  getPortalPaymentMethod,
  getPortalSubscriptions,
  PauseCycles,
  pausePortalSubscription,
  PortalPauseResponse,
  PortalPaymentMethod,
  PortalSubscription,
} from '@api/customer-portal-api';
import { queryKeys } from '@hooks/queryKeys';
import { normalizeLineOptions } from '@lib/lineOptions';

const withLineOptions = (sub: PortalSubscription): PortalSubscription => {
  const next = { ...sub };
  if (sub.line_item) {
    next.line_item = {
      ...sub.line_item,
      selected_options: normalizeLineOptions(sub.line_item.selected_options),
    };
  }
  if (Array.isArray(sub.line_items)) {
    next.line_items = sub.line_items.map((item) => ({
      ...item,
      selected_options: normalizeLineOptions(item.selected_options),
    }));
  }
  return next;
};

export const useSubscriptions = () =>
  useQuery({
    queryKey: queryKeys.subscriptions.all,
    queryFn: async (): Promise<PortalSubscription[]> => {
      const data = await getPortalSubscriptions();
      return Array.isArray(data) ? data.map(withLineOptions) : [];
    },
  });

interface CancelInput {
  subscriptionId: string;
  cancellationMode?: 'at_period_end' | 'immediate';
}

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, cancellationMode = 'at_period_end' }: CancelInput) =>
      cancelPortalSubscription(subscriptionId, cancellationMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
    },
  });
};

interface RescheduleInput {
  subscriptionId: string;
  freeDays: number;
}

export const useRescheduleSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, freeDays }: RescheduleInput) =>
      addFreeDaysToPortalSubscription(subscriptionId, freeDays),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
    },
  });
};

export const usePauseOptions = (subscriptionId: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.subscriptions.pauseOptions(subscriptionId),
    queryFn: () => getPortalPauseOptions(subscriptionId),
    enabled,
    retry: false,
    staleTime: 0,
  });

interface PauseInput {
  subscriptionId: string;
  cycles: PauseCycles;
}

export const usePauseSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation<PortalPauseResponse, unknown, PauseInput>({
    mutationFn: ({ subscriptionId, cycles }) => pausePortalSubscription(subscriptionId, cycles),
    // On every outcome: a 409 or an unconfirmed 502 may still mean the subscription changed.
    // Returned, so the mutation stays pending (and the caller's callbacks wait) until the list
    // shows the new state.
    onSettled: (_data, error, { subscriptionId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all, exact: true }),
        // After a failure the pop-up re-reads its options to show why. After a success it
        // closes, and a re-read would only 409 already_paused while it does.
        error !== null
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.subscriptions.pauseOptions(subscriptionId),
            })
          : null,
      ]),
  });
};

export const usePaymentMethod = (subscriptionId: string) =>
  useQuery<PortalPaymentMethod | null>({
    queryKey: queryKeys.paymentMethod.bySubscription(subscriptionId),
    queryFn: () => getPortalPaymentMethod(subscriptionId),
    retry: false,
    staleTime: 5 * 60_000,
  });
